import type { HealthMetrics } from '~/types'
import type { Merchant } from '../../shared/types/merchants'
import type { Settlement } from '../../shared/types/settlements'
import type { CurrencyCode } from '../../shared/types/common'
import { generateMerchants } from './merchants'
import { generateWallets, type InternalWallet } from './wallets'
import { generateLinkedTransactions } from './transactions'
import { buildRelationships, type LinkedTransaction } from './relationships'
import { generateSettlements } from './settlements'

// Re-export generators and types
export { generateMerchants, toMerchantListItem, toMerchantWithMetrics } from './merchants'
export { generateWallets, toWalletListItem } from './wallets'
export {
  generateTransactions,
  generateLinkedTransactions,
  generateTransactionSummary,
  generateTransactionListResponse,
  generateFreshTransactions
} from './transactions'
export { buildRelationships, type LinkedTransaction } from './relationships'
export { generateSettlements, toSettlementListItem, getSettlementTransactions } from './settlements'

/**
 * Mock data store - holds all generated data with relationships
 */
export interface MockDataStore {
  merchants: Merchant[]
  wallets: InternalWallet[]
  transactions: LinkedTransaction[]
  settlements: Settlement[]
  initialized: boolean
}

// In-memory data store
let dataStore: MockDataStore | null = null

/**
 * Initialize all mock data with proper relationships
 */
export function initializeMockData(): MockDataStore {
  if (dataStore?.initialized) {
    return dataStore
  }

  console.log('[MSW] Initializing mock data...')

  // Generate merchants first
  const merchants = generateMerchants()
  console.log(`[MSW] Generated ${merchants.length} merchants`)

  // Generate wallets linked to merchants
  const wallets = generateWallets(merchants)
  console.log(`[MSW] Generated ${wallets.length} wallets`)

  // Build merchant -> wallets mapping
  const walletsByMerchant: Record<string, string[]> = {}
  for (const wallet of wallets) {
    if (!walletsByMerchant[wallet.merchantId]) {
      walletsByMerchant[wallet.merchantId] = []
    }
    walletsByMerchant[wallet.merchantId]!.push(wallet.id)
  }

  // Generate linked transactions
  const merchantIds = merchants.map(m => m.id)
  const transactions = generateLinkedTransactions(merchantIds, walletsByMerchant)
  console.log(`[MSW] Generated ${transactions.length} transactions`)

  // Build relationships and update statistics
  buildRelationships(merchants, wallets, transactions)
  console.log('[MSW] Built relationships and updated statistics')

  // Build wallet data with currency for settlements
  const walletsByMerchantWithCurrency: Record<string, Array<{ id: string, currency: CurrencyCode }>> = {}
  for (const wallet of wallets) {
    if (!walletsByMerchantWithCurrency[wallet.merchantId]) {
      walletsByMerchantWithCurrency[wallet.merchantId] = []
    }
    walletsByMerchantWithCurrency[wallet.merchantId]!.push({
      id: wallet.id,
      currency: wallet.currency
    })
  }

  // Build transactions by wallet for settlements
  const transactionsByWallet: Record<string, string[]> = {}
  for (const tx of transactions) {
    if (!transactionsByWallet[tx.walletId]) {
      transactionsByWallet[tx.walletId] = []
    }
    transactionsByWallet[tx.walletId]!.push(tx.chargeUuid)
  }

  // Generate settlements linked to merchants, wallets, and transactions
  const settlements = generateSettlements(merchantIds, walletsByMerchantWithCurrency, transactionsByWallet)
  console.log(`[MSW] Generated ${settlements.length} settlements`)

  dataStore = {
    merchants,
    wallets,
    transactions,
    settlements,
    initialized: true
  }

  return dataStore
}

/**
 * Get mock data store (initializes if needed)
 */
export function getMockData(): MockDataStore {
  if (!dataStore?.initialized) {
    return initializeMockData()
  }
  return dataStore
}

/**
 * Reset mock data (for testing)
 */
export function resetMockData(): void {
  dataStore = null
}

// Legacy dashboard summary type (deprecated - use AnalyticsSummary from analytics handlers instead)
interface LegacyDashboardSummary {
  transactionsToday: number
  totalTransactions: number
  volumeToday: number
  totalVolume: number
  successRate: number
  successRateGrowth: number
  pendingSettlements: number
  scheduledSettlements: number
  activeWallets: number
  activeWalletsToday: number
}

/**
 * Generate mock dashboard summary data
 * @deprecated Use analytics/summary endpoint instead
 */
export function generateDashboardSummary(): LegacyDashboardSummary {
  const baseTransactions = Math.floor(Math.random() * 30000) + 10000
  const todayTransactions = Math.floor(Math.random() * 2000) + 500

  const baseVolume = Math.floor(Math.random() * 5000000) + 2000000
  const todayVolume = Math.floor(Math.random() * 500000) + 100000

  const baseWallets = Math.floor(Math.random() * 300000) + 200000
  const todayWallets = Math.floor(Math.random() * 500) + 50

  return {
    transactionsToday: todayTransactions,
    totalTransactions: baseTransactions,
    volumeToday: todayVolume,
    totalVolume: baseVolume,
    successRate: Math.random() * 0.4 + 0.3, // 30% - 70%
    successRateGrowth: (Math.random() - 0.5) * 0.02, // -1% to +1%
    pendingSettlements: Math.floor(Math.random() * 15000) + 5000,
    scheduledSettlements: Math.floor(Math.random() * 25000) + 10000,
    activeWallets: baseWallets,
    activeWalletsToday: todayWallets
  }
}

// Dynamic health metrics for demo purposes
class DynamicHealthMetrics {
  private currentMetrics: HealthMetrics
  private lastUpdated = Date.now()
  private trends: Record<string, number[]> = {
    responseTime: [],
    successRate: [],
    errorRate: [],
    timeoutRate: [],
    uptime: []
  }

  constructor() {
    this.currentMetrics = this.generateBaseMetrics()
  }

  private generateBaseMetrics(): HealthMetrics {
    const successRate = Math.random() * 15 + 85 // 85-100%
    const errorRate = Math.random() * 5 // 0-5%
    const timeoutRate = Math.random() * 2 // 0-2%

    let systemStatus: HealthMetrics['systemStatus'] = 'healthy'
    if (successRate < 90 || errorRate > 3 || timeoutRate > 1) {
      systemStatus = 'warning'
    }
    if (successRate < 85 || errorRate > 5 || timeoutRate > 2) {
      systemStatus = 'critical'
    }

    return {
      responseTime: Math.random() * 200 + 50, // 50-250ms
      successRate,
      errorRate,
      timeoutRate,
      systemStatus,
      uptime: Math.random() * 1 + 99, // 99-100%
      lastUpdated: new Date().toISOString()
    }
  }

  public getCurrentMetrics(): HealthMetrics {
    this.maybeUpdateMetrics()
    return { ...this.currentMetrics }
  }

  private maybeUpdateMetrics(): void {
    const now = Date.now()
    const timeSinceUpdate = now - this.lastUpdated

    // Update every 3-8 seconds with some variability
    const shouldUpdate = timeSinceUpdate > 3000 + Math.random() * 5000

    if (shouldUpdate) {
      this.updateMetrics()
      this.lastUpdated = now
    }
  }

  private updateMetrics(): void {
    const isDemoMode = typeof sessionStorage !== 'undefined'
      && sessionStorage.getItem('demo-mode') === 'true'

    if (isDemoMode) {
      // In demo mode, create more dramatic variations
      this.simulateDemoScenario()
    } else {
      // Normal variations
      this.simulateNormalVariations()
    }

    this.currentMetrics.lastUpdated = new Date().toISOString()
  }

  private simulateDemoScenario(): void {
    // Simulate realistic but noticeable changes for demo
    const scenario = Math.random()

    if (scenario < 0.1) {
      // 10% chance: Simulate a brief spike in response time and errors
      this.currentMetrics.responseTime = Math.random() * 500 + 200 // 200-700ms
      this.currentMetrics.errorRate = Math.random() * 8 + 2 // 2-10%
      this.currentMetrics.successRate = 100 - this.currentMetrics.errorRate
      this.currentMetrics.systemStatus = 'warning'

      // Minimal variance for other metrics
      this.currentMetrics.timeoutRate += (Math.random() - 0.5) * 0.1 // ±0.05%
      this.currentMetrics.timeoutRate = Math.max(0, Math.min(3, this.currentMetrics.timeoutRate))

      this.currentMetrics.uptime += (Math.random() - 0.5) * 0.05 // ±0.025%
      this.currentMetrics.uptime = Math.max(99, Math.min(100, this.currentMetrics.uptime))
    } else if (scenario < 0.05) {
      // 5% chance: Critical scenario
      this.currentMetrics.responseTime = Math.random() * 800 + 500 // 500-1300ms
      this.currentMetrics.errorRate = Math.random() * 15 + 5 // 5-20%
      this.currentMetrics.timeoutRate = Math.random() * 5 + 1 // 1-6%
      this.currentMetrics.successRate = 100 - this.currentMetrics.errorRate
      this.currentMetrics.systemStatus = 'critical'

      // Minimal variance for uptime even in critical state
      this.currentMetrics.uptime += (Math.random() - 0.5) * 0.1 // ±0.05%
      this.currentMetrics.uptime = Math.max(98, Math.min(100, this.currentMetrics.uptime))
    } else {
      // Normal healthy state with varied response times
      this.simulateNormalVariations()
      this.currentMetrics.systemStatus = 'healthy'
    }
  }

  private simulateNormalVariations(): void {
    // More variance in response time to show network fluctuations (±50ms)
    this.currentMetrics.responseTime += (Math.random() - 0.5) * 100 // ±50ms
    this.currentMetrics.responseTime = Math.max(30, Math.min(300, this.currentMetrics.responseTime))

    // Minimal variance for success rate (±0.2%)
    this.currentMetrics.successRate += (Math.random() - 0.5) * 0.4 // ±0.2%
    this.currentMetrics.successRate = Math.max(95, Math.min(100, this.currentMetrics.successRate))

    // Minimal variance for error rate (±0.2%)
    this.currentMetrics.errorRate += (Math.random() - 0.5) * 0.4 // ±0.2%
    this.currentMetrics.errorRate = Math.max(0, Math.min(5, this.currentMetrics.errorRate))

    // Minimal variance for timeout rate (±0.05%)
    this.currentMetrics.timeoutRate += (Math.random() - 0.5) * 0.1 // ±0.05%
    this.currentMetrics.timeoutRate = Math.max(0, Math.min(2, this.currentMetrics.timeoutRate))

    // Minimal variance for uptime (±0.02%)
    this.currentMetrics.uptime += (Math.random() - 0.5) * 0.04 // ±0.02%
    this.currentMetrics.uptime = Math.max(99.5, Math.min(100, this.currentMetrics.uptime))

    // Determine system status
    if (this.currentMetrics.successRate < 90 || this.currentMetrics.errorRate > 3) {
      this.currentMetrics.systemStatus = 'warning'
    } else if (this.currentMetrics.successRate < 85 || this.currentMetrics.errorRate > 5) {
      this.currentMetrics.systemStatus = 'critical'
    } else {
      this.currentMetrics.systemStatus = 'healthy'
    }
  }
}

// Export singleton instance
export const dynamicHealthMetrics = new DynamicHealthMetrics()
